import apiConfig from 'utils/api/config';
import React, { useEffect, useMemo, useState } from 'react';
import { translate } from 'components/Translation';
import ServicesAccordion from '../common/ServicesAccordion';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { decode } from 'utils/crypto/crypto';
import AccordionContent from '../common/AccordionContent';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { getStatus, isPackageAllowed } from 'utils/packages/packageState';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { SERVICE_STATUS } from '../../constants';

interface IFBBServicesProps {
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
		};
	};
	accountName: string;
	showOnlyActive: boolean;
	subscriberProducts: SingleSubscriberProduct[];
	updateOrderItems: types.services.updateOrderItemsFunction;
}

const FBBServices = ({
	match,
	accountName,
	showOnlyActive,
	subscriberProducts,
	updateOrderItems,
}: IFBBServicesProps) => {
	const { phoneNumberEncoded } = match.params;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const [packages, setPackages] = useState<SinglePackage[]>([]);

	const updatePackage = (singlePackage: types.services.IPackage) => {
		const action = singlePackage.status?.type === 'ACTIVE' ? 'TURN_OFF' : 'TURN_ON';
		const orderItemAction: 'REMOVE' | 'ADD' = singlePackage.status?.type === 'ACTIVE' ? 'REMOVE' : 'ADD';
		const serviceName = singlePackage.name;
		const orderItems = [
			{
				id: singlePackage.legacyProductId,
				action: orderItemAction,
				place: singlePackage.place,
			},
		];
		updateOrderItems(orderItems, action, serviceName);
	};

	useEffect(() => {
		ECommerceService.getPackages('ALL', 'FBB').subscribe(({ packages }) => {
			setPackages(
				packages.filter((singlePackage: SinglePackage) => isPackageAllowed(subscriberProducts, singlePackage))
			);
		});
		return () => {};
	}, [subscriberProducts]);
	const accordionPackages: types.services.IPackage[] = useMemo(() => {
		return packages.map((singlePackage) => {
			const getPackageStatus = getStatus(subscriberProducts, singlePackage);
			return {
				name: singlePackage.name,
				id: singlePackage.partNumber,
				status: {
					type: getPackageStatus,
				},
				legacyProductId: singlePackage.partNumber,
				ebuConfigProduct: singlePackage.ebuConfigProduct,
				place: singlePackage.place,
				description: translate(
					'services.fix.description.fee',
					singlePackage.installationFee || '-',
					singlePackage.monthlyFee || '-'
				),
				buttons:
					singlePackage.selfActivate &&
					(getPackageStatus === SERVICE_STATUS.ACTIVE || getPackageStatus === SERVICE_STATUS.INACTIVE)
						? [
								{
									text:
										getPackageStatus === SERVICE_STATUS.ACTIVE
											? translate('numbers.services.turn.off')
											: translate('numbers.services.turn.on'),
									color:
										getPackageStatus === SERVICE_STATUS.ACTIVE ? 'tertiary-light' : 'primary-light',
									type: getPackageStatus === SERVICE_STATUS.ACTIVE ? 'TURN_OFF' : 'TURN_ON',
								},
						  ]
						: [],
			};
		});
	}, [subscriberProducts, packages]);

	const filteredAccordionPackages = useMemo(() => {
		if (showOnlyActive) {
			return accordionPackages.filter((singlePackage) => singlePackage.status?.type === 'ACTIVE');
		}
		return accordionPackages;
	}, [accordionPackages, showOnlyActive]);

	return (
		<>
			<BreadcrumbsItem to={match.path}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.services'
			)}`}</BreadcrumbsItem>
			<ServicesAccordion
				active={0}
				collapses={[
					{
						title: translate('number.services.additional.services'),
						content: (
							<AccordionContent
								elements={filteredAccordionPackages}
								termsAndConditions={{
									type: 'link',
									url: apiConfig.links.additionalServices,
								}}
								updateService={updatePackage}
							/>
						),
					},
				]}
			/>
		</>
	);
};

export default FBBServices;
